/**
 * Module Flags Page - SYSTEM only
 * 
 * PR-5X-E: Phase 5X Modul-System
 * 
 * UX Pattern: Wie MachinesPage
 * - Row-Click oeffnet Edit-Modal
 * - Kein Inline-Edit
 * - Kein Zahnrad/Disketten-Icon
 * 
 * SYSTEM kann Overrides setzen/entfernen.
 */

import { useState } from 'react';
import { useAuth } from '../../App';
import { AdminLayout } from '../../components/AdminLayout';
import { useModules, ModuleMaturity, MATURITY_STYLES } from '../../hooks/useModules';
import {
  Settings,
  RotateCcw,
  ShieldAlert,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';

interface ModuleData {
  key: string;
  name: string;
  description: string;
  tooltip: string;
  enabled: boolean;
  maturity: ModuleMaturity;
  hasOverride: boolean;
  overrideEnabled: boolean | null;
  overrideMaturity: ModuleMaturity | null;
  overrideTooltip: string | null;
  defaultEnabled: boolean;
  defaultMaturity: ModuleMaturity;
}

export default function ModuleFlagsPage() {
  const { user } = useAuth();
  const { modules, loading, error, refresh } = useModules();
  
  // Modal State
  const [editingModule, setEditingModule] = useState<ModuleData | null>(null);
  const [formEnabled, setFormEnabled] = useState<boolean | null>(null);
  const [formMaturity, setFormMaturity] = useState<ModuleMaturity | null>(null);
  const [formTooltip, setFormTooltip] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // RBAC: Nur SYSTEM
  if (!user || user.role !== 'SYSTEM') {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="bg-white rounded-lg border p-8 text-center max-w-md">
            <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900">Kein Zugriff</h1>
            <p className="text-slate-500 mt-2">Nur SYSTEM-Benutzer duerfen Module verwalten.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const openEditDialog = (module: ModuleData) => {
    setEditingModule(module);
    setFormEnabled(module.overrideEnabled);
    setFormMaturity(module.overrideMaturity);
    setFormTooltip(module.overrideTooltip ?? '');
    setSaveError(null);
  };

  const closeDialog = () => {
    setEditingModule(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingModule) return;
    
    setSaving(true);
    setSaveError(null);
    
    try {
      const response = await fetch(`/api/system/modules/${editingModule.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          enabled: formEnabled, 
          maturity: formMaturity,
          tooltip: formTooltip || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Speichern fehlgeschlagen');
      }

      await refresh();
      closeDialog();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editingModule) return;
    
    setSaving(true);
    setSaveError(null);
    
    try {
      const response = await fetch(`/api/system/modules/${editingModule.key}/reset`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Zuruecksetzen fehlgeschlagen');
      }

      await refresh();
      closeDialog();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Module und BETA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Module zur Laufzeit aktivieren/deaktivieren und Reifegrad setzen.
          </p>
        </div>

        {/* Hinweis */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Hinweis:</strong> Deaktivierte Module werden fuer Nicht-SYSTEM-Benutzer komplett ausgeblendet.
            BETA/EXPERIMENTAL-Module zeigen einen Badge in der Navigation.
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">Fehler: {error}</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Modul</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Reifegrad</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Override</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {modules.map((module) => (
                  <tr 
                    key={module.key}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => openEditDialog(module as ModuleData)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{module.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{module.key}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={module.enabled ? 'text-green-600' : 'text-red-600'}>
                        {module.enabled ? <Check className="w-5 h-5 mx-auto" /> : <X className="w-5 h-5 mx-auto" />}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded border ${MATURITY_STYLES[module.maturity]}`}>
                        {module.maturity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      {module.hasOverride ? (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Ja</span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingModule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                Modul bearbeiten: {editingModule.name}
              </h2>
              <button 
                onClick={closeDialog}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {saveError}
                </div>
              )}

              <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                <div><strong>Key:</strong> <code className="text-xs">{editingModule.key}</code></div>
                <div><strong>Default:</strong> {editingModule.defaultEnabled ? 'Aktiviert' : 'Deaktiviert'}, {editingModule.defaultMaturity}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status
                </label>
                <select
                  value={formEnabled === null ? 'null' : formEnabled ? 'true' : 'false'}
                  onChange={(e) => setFormEnabled(e.target.value === 'null' ? null : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="null">Default ({editingModule.defaultEnabled ? 'Aktiviert' : 'Deaktiviert'})</option>
                  <option value="true">Aktiviert</option>
                  <option value="false">Deaktiviert</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reifegrad
                </label>
                <select
                  value={formMaturity ?? 'null'}
                  onChange={(e) => setFormMaturity(e.target.value === 'null' ? null : e.target.value as ModuleMaturity)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="null">Default ({editingModule.defaultMaturity})</option>
                  <option value="STABLE">STABLE</option>
                  <option value="BETA">BETA</option>
                  <option value="EXPERIMENTAL">EXPERIMENTAL</option>
                  <option value="DEPRECATED">DEPRECATED</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Tooltip (optional)
                </label>
                <input
                  type="text"
                  value={formTooltip}
                  onChange={(e) => setFormTooltip(e.target.value)}
                  placeholder="z.B. BETA: Funktion im Aufbau..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Wird im Badge-Tooltip angezeigt. Leer = Default-Tooltip.
                </p>
              </div>
            </div>
            
            <div className="flex justify-between gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <div>
                {editingModule.hasOverride && (
                  <button
                    onClick={handleReset}
                    disabled={saving}
                    className="px-3 py-2 text-sm border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 disabled:opacity-50 flex items-center gap-1"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Auf Default
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
